import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, ChevronDown, Check, Upload, ImageIcon, Loader2 } from 'lucide-react';
import { useRaces } from '../../hooks';

/**
 * Konfigurasi tier dengan warna dan styling
 */
export const TIER_OPTIONS = [
    {
        value: 'common',
        label: 'Common',
        dot: 'bg-gray-400',
        text: 'text-gray-500',
        bg: 'bg-gray-500/10',
        activeBg: 'bg-gray-500/20',
        border: 'border-gray-400',
    },
    {
        value: 'uncommon',
        label: 'Uncommon',
        dot: 'bg-emerald-400',
        text: 'text-emerald-500',
        bg: 'bg-emerald-500/10',
        activeBg: 'bg-emerald-500/20',
        border: 'border-emerald-400',
    },
    {
        value: 'rare',
        label: 'Rare',
        dot: 'bg-blue-400',
        text: 'text-blue-500',
        bg: 'bg-blue-500/10',
        activeBg: 'bg-blue-500/20',
        border: 'border-blue-400',
    },
    {
        value: 'epic',
        label: 'Epic',
        dot: 'bg-purple-400',
        text: 'text-purple-500',
        bg: 'bg-purple-500/10',
        activeBg: 'bg-purple-500/20',
        border: 'border-purple-400',
    },
    {
        value: 'legend',
        label: 'Legend',
        dot: 'bg-yellow-400',
        text: 'text-yellow-500',
        bg: 'bg-yellow-500/10',
        activeBg: 'bg-yellow-500/20',
        border: 'border-yellow-400',
    },
    {
        value: 'mythical',
        label: 'Mythical',
        dot: 'bg-red-400',
        text: 'text-red-500',
        bg: 'bg-red-500/10',
        activeBg: 'bg-red-500/20',
        border: 'border-red-400',
    },
    {
        value: 'divine',
        label: 'Divine',
        dot: 'divine-dot',
        text: 'divine-text',
        bg: 'divine-bg',
        activeBg: 'divine-bg-active',
        border: 'border-fuchsia-400',
        isDivine: true,
    },
];

/**
 * Inline styles for divine tier animations
 */
export const divineStyles = `
    .divine-dot {
        background: linear-gradient(135deg, #06b6d4, #ffffff, #d946ef);
        background-size: 200% 200%;
        animation: divine-shimmer 2s ease-in-out infinite;
    }
    .divine-text {
        background: linear-gradient(90deg, #06b6d4, #ffffff, #d946ef);
        background-size: 200% 200%;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        animation: divine-shimmer 2s ease-in-out infinite;
        font-weight: 700;
    }
    .divine-bg {
        background: linear-gradient(135deg, rgba(6,182,212,0.1), rgba(217,70,239,0.1));
    }
    .divine-bg-active {
        background: linear-gradient(135deg, rgba(6,182,212,0.2), rgba(217,70,239,0.2));
        box-shadow: 0 0 12px rgba(217,70,239,0.15), 0 0 12px rgba(6,182,212,0.15);
    }
    .divine-ring {
        box-shadow: 0 0 0 2px rgba(6,182,212,0.5), 0 0 8px rgba(217,70,239,0.3);
    }
    @keyframes divine-shimmer {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
    }
    .modal-scroll {
        scrollbar-width: none;
        -ms-overflow-style: none;
    }
    .modal-scroll::-webkit-scrollbar {
        display: none;
    }
`;

// ============================================================
// Reusable Custom Searchable Dropdown (portal-based)
// ============================================================
export const SearchableDropdown = ({
    value,
    onChange,
    options,        // [{ id, label, dot?, text? }]
    placeholder,
    searchPlaceholder,
    isDarkMode,
    noResultText = 'No results found',
    renderOption,   // optional custom renderer
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const inputRef = useRef(null);

    const selected = options.find(o => String(o.id) === String(value));

    const filtered = options.filter(opt =>
        opt.label.toLowerCase().includes(search.toLowerCase())
    );

    // Calculate position when opening
    const updatePosition = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const menuHeight = 220; // approximate max height
        const openAbove = spaceBelow < menuHeight && rect.top > menuHeight;

        setMenuPos({
            top: openAbove ? rect.top - menuHeight : rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            openAbove,
        });
    };

    const handleOpen = () => {
        updatePosition();
        setIsOpen(true);
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setSearch('');
            }
        };

        const handleScroll = () => updatePosition();

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleScroll);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleScroll);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (id) => {
        onChange(id);
        setIsOpen(false);
        setSearch('');
    };

    const triggerBase = `
        w-full px-3 py-2 rounded-lg text-xs transition-colors outline-none
        flex items-center justify-between cursor-pointer
        ${isDarkMode
            ? 'bg-slate-700/50 border border-slate-600 text-slate-100 hover:border-indigo-500'
            : 'bg-slate-50 border border-slate-200 text-slate-800 hover:border-indigo-500'
        }
        ${isOpen ? 'border-indigo-500' : ''}
    `;

    return (
        <div className="relative">
            {/* Trigger */}
            <button ref={triggerRef} type="button" onClick={() => isOpen ? setIsOpen(false) : handleOpen()} className={triggerBase}>
                <div className="flex items-center gap-2 min-w-0">
                    {selected ? (
                        renderOption ? renderOption(selected, false) : (
                            <>
                                {selected.dot && <span className={`w-2 h-2 rounded-full shrink-0 ${selected.dot}`} />}
                                <span className={`truncate ${selected.text || (isDarkMode ? 'text-slate-200' : 'text-slate-700')}`}>{selected.label}</span>
                            </>
                        )
                    ) : (
                        <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>{placeholder}</span>
                    )}
                </div>
                <ChevronDown
                    size={12}
                    className={`shrink-0 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                />
            </button>

            {/* Dropdown - rendered via Portal */}
            {isOpen && createPortal(
                <div
                    ref={menuRef}
                    style={{
                        position: 'fixed',
                        top: menuPos.top,
                        left: menuPos.left,
                        width: menuPos.width,
                        zIndex: 9999,
                    }}
                    className={`
                        rounded-lg shadow-xl border overflow-hidden
                        ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
                    `}
                >
                    {/* Search */}
                    <div className={`px-2 pt-2 pb-1 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={searchPlaceholder || 'Search...'}
                            className={`
                                w-full px-2.5 py-1.5 rounded text-xs outline-none
                                ${isDarkMode
                                    ? 'bg-slate-700 text-slate-100 placeholder-slate-500'
                                    : 'bg-slate-50 text-slate-800 placeholder-slate-400'
                                }
                            `}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    setIsOpen(false);
                                    setSearch('');
                                }
                                if (e.key === 'Enter' && filtered.length === 1) {
                                    handleSelect(filtered[0].id);
                                }
                            }}
                        />
                    </div>

                    {/* Options */}
                    <div className="max-h-48 overflow-auto modal-scroll">
                        {filtered.length === 0 ? (
                            <div className={`px-3 py-3 text-xs text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {noResultText}
                            </div>
                        ) : (
                            filtered.map((opt) => {
                                const isActive = String(value) === String(opt.id);
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => handleSelect(opt.id)}
                                        className={`
                                            w-full px-3 py-2 flex items-center justify-between text-xs
                                            transition-colors cursor-pointer
                                            ${isActive
                                                ? (opt.activeBg || (isDarkMode ? 'bg-slate-700' : 'bg-indigo-50'))
                                                : (isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50')
                                            }
                                        `}
                                    >
                                        <div className="flex items-center gap-2">
                                            {renderOption ? renderOption(opt, isActive) : (
                                                <>
                                                    {opt.dot && <span className={`w-2 h-2 rounded-full shrink-0 ${opt.dot}`} />}
                                                    <span className={`font-medium ${opt.text || (isDarkMode ? 'text-slate-200' : 'text-slate-700')}`}>{opt.label}</span>
                                                </>
                                            )}
                                        </div>
                                        {isActive && (
                                            <Check size={12} className={opt.isDivine ? 'text-fuchsia-400' : (isDarkMode ? 'text-indigo-400' : 'text-indigo-500')} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

// ============================================================
// ImageUpload Component (compact, supports paste)
// ============================================================
export const ImageUpload = ({ imageUrl, onImageChange, onUpload, isDarkMode, uploading }) => {
    const fileInputRef = useRef(null);
    const containerRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // Sync previewUrl with imageUrl prop
    useEffect(() => {
        if (imageUrl && !previewUrl) {
            setPreviewUrl(imageUrl);
        }
    }, [imageUrl]);

    const handleFile = async (file) => {
        if (!file || !file.type.startsWith('image/')) return;

        const reader = new FileReader();
        reader.onload = (e) => setPreviewUrl(e.target.result);
        reader.readAsDataURL(file);

        try {
            const url = await onUpload(file);
            onImageChange(url);
        } catch (err) {
            setPreviewUrl(null);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        handleFile(file);
    };

    // Ctrl+V paste support
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (const item of items) {
            if (item.type.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                handleFile(file);
                return;
            }
        }
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        setPreviewUrl(null);
        onImageChange('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div
            ref={containerRef}
            tabIndex={0}
            onPaste={handlePaste}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`
                relative w-16 h-16 rounded-lg border-2 border-dashed cursor-pointer shrink-0
                transition-all overflow-hidden flex items-center justify-center
                focus:outline-none focus:ring-2 focus:ring-indigo-500/50
                ${isDarkMode
                    ? 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                    : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                }
            `}
            title="Click, drag, or Ctrl+V to add image"
        >
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0])}
            />

            {previewUrl ? (
                <>
                    <img
                        src={previewUrl}
                        alt="Preview"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    {uploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 size={14} className="animate-spin text-white" />
                        </div>
                    )}
                    {!uploading && (
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="absolute top-0 right-0 w-4 h-4 bg-rose-500 hover:bg-rose-600 rounded-bl-md flex items-center justify-center transition-colors z-10"
                        >
                            <X size={8} className="text-white" />
                        </button>
                    )}
                </>
            ) : (
                <div className="flex flex-col items-center gap-0.5 pointer-events-none">
                    {uploading ? (
                        <Loader2 size={16} className={`animate-spin ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                    ) : (
                        <>
                            <ImageIcon size={16} className={isDarkMode ? 'text-slate-500' : 'text-slate-400'} />
                            <span className={`text-[7px] leading-tight text-center ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                                Ctrl+V
                            </span>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================
// AddCharacterModal
// ============================================================
const AddCharacterModal = ({ isOpen, onClose, onSubmit, onUploadImage, isDarkMode }) => {
    const { races, loading: racesLoading, addRace } = useRaces();

    const [form, setForm] = useState({
        name: '',
        race_id: '',
        title: '',
        tier: 'common',
        image: '',
    });

    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    // State untuk menambah ras baru
    const [showAddRace, setShowAddRace] = useState(false);
    const [newRaceName, setNewRaceName] = useState('');
    const [addingRace, setAddingRace] = useState(false);

    if (!isOpen) return null;

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleAddRace = async () => {
        if (!newRaceName.trim()) return;

        setAddingRace(true);
        const result = await addRace(newRaceName.trim());
        setAddingRace(false);

        if (result && !result.error) {
            setForm(prev => ({ ...prev, race_id: String(result.id) }));
            setNewRaceName('');
            setShowAddRace(false);
        } else {
            setError(result?.error || 'Failed to add race');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim()) return setError('Name is required');
        if (!form.race_id) return setError('Race is required');
        if (!form.title.trim()) return setError('Title is required');

        setSubmitting(true);
        setError('');

        const success = await onSubmit({
            name: form.name.trim(),
            race_id: parseInt(form.race_id, 10),
            title: form.title.trim(),
            tier: form.tier,
            image: form.image || null,
        });

        setSubmitting(false);

        if (success) {
            setForm({ name: '', race_id: '', title: '', tier: 'common', image: '' });
            onClose();
        }
    };

    // -- Shared styles --
    const inputClass = `
        w-full px-3 py-2 rounded-lg text-xs transition-colors outline-none
        ${isDarkMode
            ? 'bg-slate-700/50 border border-slate-600 text-slate-100 focus:border-indigo-500 placeholder-slate-500'
            : 'bg-slate-50 border border-slate-200 text-slate-800 focus:border-indigo-500 placeholder-slate-400'
        }
    `;

    const labelClass = `block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`;

    // -- Race options for SearchableDropdown --
    const raceOptions = races.map(r => ({ id: String(r.id), label: r.name }));

    // -- Tier options from module-level constant --

    return createPortal(
        <div
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <style>{divineStyles}</style>

            <div
                className={`
                    w-full max-w-md rounded-2xl shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto modal-scroll
                    ${isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-800'}
                `}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className={`
                    flex justify-between items-center px-5 py-3.5 border-b sticky top-0 z-10
                    ${isDarkMode ? 'border-slate-700/50 bg-slate-900' : 'border-slate-100 bg-white'}
                `}>
                    <h3 className="font-semibold text-xs">Add New Character</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className={`
                            p-1.5 rounded-full transition-colors cursor-pointer
                            ${isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}
                        `}
                    >
                        <X size={14} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-5 space-y-3.5">
                    {/* Image + Name (side by side) */}
                    <div>
                        <label className={labelClass}>Character</label>
                        <div className="flex gap-2.5 items-start">
                            <ImageUpload
                                imageUrl={form.image}
                                onImageChange={(url) => handleChange('image', url)}
                                onUpload={async (file) => {
                                    setUploading(true);
                                    try {
                                        const url = await onUploadImage(file);
                                        setUploading(false);
                                        return url;
                                    } catch (err) {
                                        setUploading(false);
                                        setError(err.message || 'Upload failed');
                                        throw err;
                                    }
                                }}
                                uploading={uploading}
                                isDarkMode={isDarkMode}
                            />
                            <div className="flex-1 space-y-1.5">
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="Name"
                                    className={inputClass}
                                    autoFocus
                                />
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="Title, e.g. Thunder Guardian"
                                    className={inputClass}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Race - Custom Dropdown + Add New */}
                    <div>
                        <label className={labelClass}>Race</label>
                        {!showAddRace ? (
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <SearchableDropdown
                                        value={form.race_id}
                                        onChange={(id) => handleChange('race_id', id)}
                                        options={raceOptions}
                                        placeholder={racesLoading ? 'Loading...' : 'Select race'}
                                        searchPlaceholder="Search race..."
                                        noResultText="No race found"
                                        isDarkMode={isDarkMode}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowAddRace(true)}
                                    className={`
                                        px-2.5 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 shrink-0
                                        ${isDarkMode
                                            ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                            : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                                        }
                                    `}
                                    title="Add new race"
                                >
                                    <Plus size={12} />
                                    <span className="hidden sm:inline">New</span>
                                </button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newRaceName}
                                    onChange={(e) => setNewRaceName(e.target.value)}
                                    placeholder="New race name"
                                    className={`${inputClass} flex-1`}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddRace();
                                        }
                                        if (e.key === 'Escape') {
                                            setShowAddRace(false);
                                            setNewRaceName('');
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddRace}
                                    disabled={addingRace || !newRaceName.trim()}
                                    className="px-2.5 py-2 rounded-lg text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50 shrink-0"
                                >
                                    {addingRace ? '...' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowAddRace(false); setNewRaceName(''); }}
                                    className={`
                                        px-2 py-2 rounded-lg transition-colors shrink-0
                                        ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}
                                    `}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Tier - Custom Dropdown */}
                    <div>
                        <label className={labelClass}>Tier</label>
                        <SearchableDropdown
                            value={form.tier}
                            onChange={(val) => handleChange('tier', val)}
                            options={tierDropdownOptions}
                            placeholder="Select tier"
                            searchPlaceholder="Search tier..."
                            noResultText="No tier found"
                            isDarkMode={isDarkMode}
                        />
                    </div>

                    {/* Error */}
                    {error && (
                        <p className="text-xs text-rose-500 bg-rose-500/10 px-3 py-2 rounded-lg">
                            {error}
                        </p>
                    )}

                    {/* Actions */}
                    <div className={`
                        flex gap-3 pt-2 border-t
                        ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}
                    `}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`
                                flex-1 px-4 py-2 rounded-lg text-xs font-medium transition-colors
                                ${isDarkMode
                                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                                }
                            `}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || uploading}
                            className="flex-1 px-4 py-2 rounded-lg text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50 shadow-sm"
                        >
                            {submitting ? 'Adding...' : 'Add Character'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

// Pre-mapped tier options for SearchableDropdown
export const tierDropdownOptions = TIER_OPTIONS.map(t => ({
    id: t.value,
    label: t.label,
    dot: t.dot,
    text: t.text,
    activeBg: t.activeBg,
    isDivine: t.isDivine,
}));

export default AddCharacterModal;
